import React, { useState } from 'react';
import { 
  Container, 
  Typography, 
  Box,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  IconButton,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CardActions,
  Paper,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Visibility as VisibilityIcon,
  Schedule as ScheduleIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import SocialPostScheduler from '../components/SocialPostScheduler';
import AIAssistant from '../components/AIAssistant';
import AnalyticsCharts from '../components/social/AnalyticsCharts';
import MediaLibrary from '../components/social/MediaLibrary';
import ContentCalendar from '../components/social/ContentCalendar';
import { generateAnalytics } from '../services/analytics';
import { getMediaLibrary, MediaItem } from '../services/contentManagement';
import { getContentCalendar, ContentCalendarEvent } from '../services/contentManagement';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const SocialMediaPage = () => {
  const [value, setValue] = useState(0);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [youtubeTitle, setYoutubeTitle] = useState('');
  const [youtubeDescription, setYoutubeDescription] = useState('');
  const [youtubeCategory, setYoutubeCategory] = useState('');
  const [youtubeTags, setYoutubeTags] = useState('');

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleVideoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setVideoFile(event.target.files[0]);
    }
  };

  const handleClearVideo = () => {
    setVideoFile(null);
  };

  const handleClearYoutube = () => {
    setVideoFile(null);
    setYoutubeTitle('');
    setYoutubeDescription('');
    setYoutubeCategory('');
    setYoutubeTags('');
  };

  const handleYoutubeSubmit = () => {
    // Implementar lógica de envio para o YouTube
    console.log('Enviando para o YouTube...');
  };

  const metrics = {
    engagement: 75,
    followers: 5000,
    scheduledPosts: 12
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ width: '100%', mt: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={value} onChange={handleChange} aria-label="social media tabs">
            <Tab label="Dashboard" {...a11yProps(0)} />
            <Tab label="Agendamento" {...a11yProps(1)} />
            <Tab label="Histórico de Postagem" {...a11yProps(2)} />
            <Tab label="Analytics" {...a11yProps(3)} />
            <Tab label="Biblioteca de Mídia" {...a11yProps(4)} />
            <Tab label="Calendário" {...a11yProps(5)} />
            <Tab label="Assistente IA" {...a11yProps(6)} />
          </Tabs>
        </Box>

        {/* Dashboard */}
        <TabPanel value={value} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TrendingUpIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">Engajamento</Typography>
                  </Box>
                  <Typography variant="h4" color="primary">
                    {metrics.engagement}%
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={metrics.engagement} 
                    sx={{ mt: 1, height: 8, borderRadius: 4 }}
                  />
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PeopleIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">Seguidores</Typography>
                  </Box>
                  <Typography variant="h4" color="primary">
                    {metrics.followers.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total em todas as plataformas
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <VisibilityIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">Visualizações</Typography>
                  </Box>
                  <Typography variant="h4" color="primary">
                    45.2K
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Últimos 30 dias
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ScheduleIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">Agendados</Typography>
                  </Box>
                  <Typography variant="h4" color="primary">
                    {metrics.scheduledPosts}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Posts para publicação
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Agendamento */}
        <TabPanel value={value} index={1}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              Agendamento
            </Typography>
          </Box>
          <SocialPostScheduler showPostHistory={false} />
        </TabPanel>

        {/* Histórico de Postagem */}
        <TabPanel value={value} index={2}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              Histórico de Postagem
            </Typography>
          </Box>
          <SocialPostScheduler showPostForm={false} showPostHistory={true} />
        </TabPanel>

        {/* Analytics */}
        <TabPanel value={value} index={3}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              Analytics Avançado
            </Typography>
          </Box>
          <AnalyticsCharts 
            data={generateAnalytics('all')}
            platform="all"
          />
        </TabPanel>

        {/* Biblioteca de Mídia */}
        <TabPanel value={value} index={4}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              Biblioteca de Mídia
            </Typography>
          </Box>
          <MediaLibrary
            media={getMediaLibrary()}
            onSelect={(item: MediaItem) => {
              // Implementar seleção de mídia
              console.log('Mídia selecionada:', item);
            }}
            onAdd={(file: File) => {
              // Implementar adição de mídia
              console.log('Arquivo adicionado:', file);
            }}
            onDelete={(id: string) => {
              // Implementar exclusão de mídia
              console.log('Mídia excluída:', id);
            }}
          />
        </TabPanel>

        {/* Calendário */}
        <TabPanel value={value} index={5}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              Calendário de Conteúdo
            </Typography>
          </Box>
          <ContentCalendar
            events={getContentCalendar()}
            onEdit={(event: ContentCalendarEvent) => {
              // Implementar edição de evento
              console.log('Evento editado:', event);
            }}
            onDelete={(id: string) => {
              // Implementar exclusão de evento
              console.log('Evento excluído:', id);
            }}
            onApprove={(id: string) => {
              // Implementar aprovação de evento
              console.log('Evento aprovado:', id);
            }}
          />
        </TabPanel>

        {/* Assistente IA */}
        <TabPanel value={value} index={6}>
          <AIAssistant />
        </TabPanel>
      </Box>
    </Container>
  );
};

export default SocialMediaPage;